import { isProd } from '~constants'
import { GtmTypes } from '~types'
import { getFevoWidgetNameFromUrl, isExternalLink } from 'utils'

const getGtmEventName = (isTicketmasterLink: boolean) => {
  if (isTicketmasterLink) {
    return 'click_buy'
  } else {
    return 'click_content'
  }
}

export const getGtmLinkType = (url: string | null) => {
  if (!url) {
    return 'interactive'
  } else if (getFevoWidgetNameFromUrl(url)) {
    return 'fevo'
  } else if (isExternalLink(url)) {
    return 'external'
  } else {
    return 'internal'
  }
}

export const getGtmClickSource = (asPath: string) => {
  if (!asPath) return ''

  let slug = asPath
  slug = slug.split('#')[0]
  slug = slug.split('?')[0]
  return slug === '/' ? 'homepage' : slug
}

export const getEventIdFromUrl = (url: string) => {
  try {
    const { pathname } = new URL(url)
    const segments = pathname.split('/').filter(Boolean)
    const eventIndex = segments.indexOf('event')

    if (eventIndex !== -1 && segments.length > eventIndex + 1) {
      return segments[eventIndex + 1]
    }
    return ''
  } catch (e) {
    return ''
  }
}

export const fireAnalyticsEvent = (clickUrl: string | null, pageUrl: string | null, data: GtmTypes) => {
  const {
    clickPreviousPv,
    clickTracking,
    clickType,
    event,
    eventData,
    linkLocation,
    linkModule,
    linkPosition,
    pageType
  } = data || {}

  const { eventCategory, eventDate, eventType, ticketmasterEventId } = eventData || {}

  const isTMLink = isTicketmasterUrl(clickUrl)
  const isPageViewEvent = event === 'pageview'

  const sharedParams = {
    event: event || getGtmEventName(isTMLink)
  }

  const clickEventParams = {
    link_location: linkLocation || 'body',
    link_module: linkModule || '',
    link_type: getGtmLinkType(clickUrl),
    ...(pageUrl && { click_source: getGtmClickSource(pageUrl) }),
    ...(clickTracking && { click_tracking: clickTracking }),
    ...(clickType && { click_type: clickType }),
    ...(linkPosition && { link_position: linkPosition })
  }

  const pageViewEventParams = {
    brand_name: 'sphere',
    business_unit: 'entertainment',
    click_previous_pv: clickPreviousPv,
    page_type: pageType,
    venue_name: 'sphere'
  }

  const clickTMParams = {
    event_venue_name: 'sphere',
    event_date: eventDate || '',
    event_type: eventType || '',
    event_category: eventCategory || '',
    ...(pageUrl && { ticketmaster_event_id: ticketmasterEventId || getEventIdFromUrl(pageUrl) })
  }

  const params = {
    ...sharedParams,
    ...(isPageViewEvent ? { ...pageViewEventParams } : { ...clickEventParams }),
    ...(isTMLink && { ...clickTMParams })
  }

  const sanitizedParams = Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, formatAnalyticsText(value)])
  )

  if (typeof window !== 'undefined') {
    // @ts-ignore
    window.dataLayer.push(sanitizedParams)
  }
  if (!isProd) {
    console.table(sanitizedParams)
  }
}

export const getGtmLinkPosition = (ctaArr: any, currentIndex = 0) => {
  return Array.isArray(ctaArr) && ctaArr.length > 1 ? (currentIndex + 1).toString() : ''
}

export const isTicketmasterUrl = (url: string | null | undefined) => {
  if (!url) return false
  try {
    const parsedUrl = new URL(url, window.location.origin)
    const hostname = parsedUrl.hostname.toLowerCase()

    const ticketmasterDomains = [
      'ticketmaster.com',
      'ticketmaster.ca',
      'ticketmaster.co.uk',
      'ticketmaster.ie',
      'ticketmaster.com.au',
      'ticketmaster.co.nz',
      'ticketmaster.eu',
      'ticketmaster.de',
      'ticketmaster.fr',
      'ticketmaster.es',
      'ticketmaster.be',
      'ticketmaster.se',
      'ticketmaster.nl',
      'ticketmaster.dk',
      'ticketmaster.no'
    ]

    return ticketmasterDomains.some(domain => hostname === domain || hostname.endsWith(`.${domain}`))
  } catch {
    return false
  }
}

export const tagManagerArgs = {
  gtmId: 'GTM-MLJ3V4L'
}

export const getPageType = (slug: string) => {
  return slug === '/' ? 'homepage' : slug.substring(1)
}

// helper function that takes in a string and strips special characters and spaces, then joins with the delimiter
// @param text String
// @param delimeter Delimeter to join the text with
export const formatAnalyticsText = (text: string, delimiter = '_') => {
  if (typeof text !== 'string') return

  return text
    .trim() // Trim leading/trailing spaces
    .replace(/-/g, delimiter) // Convert hyphens to delimiter
    .replace(/[^\w\s:/|]/g, '') // Allow alphanumerics (\w), whitespace (\s), colons (:), slashes (/), pipes (|)
    .replace(/\s+/g, delimiter) // Replace spaces with delimiter
    .toLowerCase() // Convert to lowercase
}

// Used to prevent pageview when route changes within calendar
export const skipGtmPageview = { current: false }
